<template>
  <div class="nx-taxonomy-selector-wrapper">
    <v-autocomplete
      ref="selector"
      :menu-props="menuProps"
      :disabled="disabled"
      :items="formattedItems"
      :loading="loading"
      :search-input="searchText"
      :value="selectedTaxonomyIds"
      chips
      class="nx-taxonomy-selector"
      item-path="name"
      item-text="name"
      item-value="id"
      multiple
      no-filter
      :label="label"
      v-bind="$attrs"
      :hide-selected="limitReached && limit > 1"
      @blur="onBlur"
      @focus="$emit('focus', $event)"
      @input="acceptSelection"
      @update:searchInput="searchByText"
    >
      <template v-slot:append>
        <v-icon
          v-if="!disableSort && multiple"
          :disabled="!value || (value && value.length <= 1)"
          @click.stop.prevent="openDialog('sort')"
        >
          low_priority
        </v-icon>
        <v-icon
          class="ml-3"
          @click.stop.prevent="openDialog('searcher')"
        >
          folder
        </v-icon>
      </template>
      <template
        v-slot:selection="{ item, parent, selected }"
      >
        <v-chip
          :selected="selected"
          close
          @input="parent.selectItem(item)"
        >
          <v-avatar>
            <v-icon v-text="item.icon" />
          </v-avatar>
          <span v-text="item.name" />
          <router-link
            v-if="allowEditItems"
            class="nx-chip--edit"
            :to="generateItemRoute(item)"
            target="_blank"
          >
            <span class="v-chip__close">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon
                    class="ml-2"
                    v-on="on"
                  >edit</v-icon>
                </template>
                <span>{{ $t('message.common.edit') }}</span>
              </v-tooltip>
            </span>
          </router-link>
        </v-chip>
      </template>

      <template
        v-slot:item="{ item }"
      >
        <v-list-tile-avatar>
          <v-icon v-text="item.icon" />
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title
            v-html="$options.filters.highlight(item.name, searchText)"
          />
        </v-list-tile-content>
      </template>

      <template slot="no-data">
        <li>
          <v-list-tile>
            <v-list-tile-avatar>
              <v-icon v-if="limitReached">
                not_interested
              </v-icon>
              <v-progress-circular
                v-else-if="loading"
                :width="2"
                indeterminate
              />
              <v-icon v-else-if="searchText">
                not_interested
              </v-icon>
              <v-icon v-else>
                edit
              </v-icon>
            </v-list-tile-avatar>

            <v-list-tile-content>
              <v-list-tile-title
                v-if="limitReached && limit > 1"
                v-html="$t('message.search.selection.limitReached')"
              />
              <v-list-tile-title
                v-else-if="loading"
                v-html="$t('message.common.loading')"
              />
              <v-list-tile-title
                v-else-if="searchText"
                v-html="$t('message.search.noDataFound')"
              />
              <v-list-tile-title
                v-else
                v-html="$t('message.search.typeToSearch')"
              />
            </v-list-tile-content>
          </v-list-tile>
        </li>
      </template>
    </v-autocomplete>

    <span
      v-if="limit > 0 && limitCounter"
      class="nx-taxonomy-selector-limit grey--text"
      :class="{ reached: limit < selectedItems.length }"
    >
      {{ selectedItems.length }}/{{ limit }}
    </span>

    <nx-searcher-dialog
      v-if="dialogState.searcher.render"
      :active="dialogState.searcher.active"
      :entity="entity"
      :entity-tabs="false"
      :filters="filters"
      :limit="limit"
      :text="searchText"
      :value="searcherValue"
      :title="$t('message.searcher.name')"
      fix-toolbar
      @dismiss="closeDialog('searcher')"
      @input="acceptSearcherValue"
      @setFilters="setFilters"
    />

    <nx-entity-sortable-dialog
      v-if="dialogState.sort.render"
      :title="$t('message.entity.modal.sortableTitle', { entity: label })"
      :active="dialogState.sort.active"
      :items="selectedItems"
      @dismiss="closeDialog('sort')"
      @input="acceptSearcherValue"
    />
  </div>
</template>

<script>
import Loadable from '@/mixins/loadable';
import Searchable from '@/modules/searcher/mixins/searchable';
import Authorizable from '@/modules/authorization/mixins/authorizable';
import { toArray } from '@/utils';
import isObject from 'lodash/isObject';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';
import { typeToIcon } from '@/modules/taxonomy/config';
import { get, isEmpty } from 'lodash';
import isUndefined from 'lodash/isUndefined';
import api from '../api';
import ManageDialogs, { initDialog } from '@/mixins/manage-dialogs';

// May change depending on backend implementations
export const rootValue = '0';

/**
 * Taxonomy Selector.
 *
 * @property {number} [limit] Maximun number of taxonomies that can be selected.
 * @property {(TaxonomyCard|TaxonomyCard[])} [value] The initial selected taxonomies.
 * @property {[]} [types] Taxonomy types allowed in the module.
 */
export default {
  name: 'NxTaxonomySelector',
  components: {
    NxSearcherDialog: () => import('@/modules/searcher/SearcherDialog.vue'),
    NxEntitySortableDialog: () => import('@/modules/entity/dialogs/EntitySortableDialog.vue'),
  },
  extends: {},
  mixins: [Searchable, Loadable, Authorizable, ManageDialogs],
  props: {
    additionalFilters: {
      type: Object,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    limit: {
      type: Number,
      default: undefined,
    },
    type: {
      type: [String, Array],
      default: undefined,
    },
    siteId: {
      type: [Number, Array],
      default: undefined,
    },
    mediaId: {
      type: [Number, Array],
      default: undefined,
    },
    value: {
      type: [Array, Object, Number, String],
      default: undefined,
    },
    allowRootValue: {
      type: Boolean,
      default: false,
    },
    allowEditItems: {
      type: Boolean,
      default: true,
    },
    limitCounter: {
      type: Boolean,
      default: true,
    },
    allowLandingPage: {
      type: Boolean,
      default: true,
    },
    auth: {
      type: Object,
      default() {
        return {
          actions: {
            ADD: null, // Specify bbnx operation when adding taxonomies for validation
            DELETE: null, // Specify bbnx operation when removing current taxonomies
          },
          params: {}, // Object of params that will be sent to bbnx to validate action
        };
      },
    },
    excludedIds: {
      type: Array,
      default: () => ([]),
    },
    disabled: {
      type: Boolean,
    },
    disableSort: Boolean,
  },
  data() {
    return {
      dialogState: {
        searcher: initDialog(),
        sort: initDialog(),
      },
      entity: 'category',
      filters: {
        type: this.type,
        siteId: this.siteId || this.getDefaultSiteId(),
        mediaId: this.mediaId,
      },
      selectedItems: [],
      previousSearchItems: [],
      parentNullItem: { id: rootValue, name: this.$t('message.taxonomy.root'), icon: 'device_hub' },
      searchOnFiltersChange: false,
    };
  },
  computed: {
    currentSiteId() {
      return this.$store.getters.siteId;
    },
    limitReached() {
      return this.selectedItems.length >= this.limit;
    },
    hasSelectedItems() {
      return toArray(this.value).length > 0;
    },
    selectedTaxonomyIds() {
      return this.selectedItems.filter(item => !!item)
        .map(item => item.id);
    },
    normalizedSelectedItems() {
      if (this.limit === 1) {
        return this.selectedItems[0];
      }
      return this.selectedItems;
    },
    menuProps() {
      const menuProps = {};
      if (this.dialogState.searcher.render) {
        menuProps.zIndex = 0;
      }
      if (this.limit === 1) {
        menuProps.closeOnContentClick = true;
      }
      return menuProps;
    },
    multiple() {
      return !this.limit || this.limit > 1;
    },
    formattedItems() {
      const items = this.items
        .map(item => Object.assign({}, item.entity))
        // FIXME:
        // filtrar búsquedas se debe tratar desde API https://mgr.bitban.com/issues/72802
        .filter(item => (this.allowLandingPage ? true : !!item.publicUrl))
        .map(this.formatSelectorItem);
      if (this.hasSelectedItems) {
        return items.concat(toArray(this.selectedItems)
          .map(this.formatSelectorItem));
      }
      // Add root item on select options when item is rootValue
      if (this.allowRootValue) {
        items.unshift(this.parentNullItem);
      }

      return items;
    },
    defaultSort() {
      if (this.$store.getters.searcher[this.entity]
        && this.$store.getters.searcher[this.entity].sort) {
        return this.$store.getters.searcher[this.entity].sort.default;
      }
      return null;
    },
    searcherValue() {
      // Omit parentNullItem if present for searcher
      return this.selectedItems.filter(item => item.id !== rootValue);
    },
  },
  watch: {
    value: {
      async handler(value) {
        this.loadingStart();
        this.selectedItems = await this.fetchInitialItems(value);
        this.loadingFinish();
      },
      immediate: true,
    },
    siteId: {
      handler(value, oldValue) {
        if (!isEqual(value, oldValue)) {
          this.filters.siteId = value || this.getDefaultSiteId();
        }
      },
      immediate: true,
    },
    mediaId: {
      handler(value, oldValue) {
        if (!isEqual(value, oldValue)) {
          this.filters.mediaId = value || null;
        }
      },
      immediate: true,
    },
    currentSiteId: {
      handler(value) {
        if (!this.siteId
          && !this.mediaId
          && value) {
          this.filters.siteId = value || this.getDefaultSiteId();
        }
      },
      immediate: true,
    },
    additionalFilters: {
      handler(value) {
        if (isObject(value)) {
          this.filters = { ...this.filters, ...value };
        }
      },
      immediate: true,
    },
    type: {
      handler(value) {
        if (isEmpty(value)) {
          this.$delete(this.filters, 'type');
        } else {
          this.$set(this.filters, 'type', value);
        }
      },
      immediate: true,
    },
  },
  methods: {
    async authorizeOperation(operation, taxonomies) {
      const promises = taxonomies.map(taxonomyId => this.can(operation, {
        taxonomyId,
        ...this.auth.params, // Merge specified params by props
      }));
      const results = await Promise.all(promises);
      return results.every(result => result === true);
    },
    async rejectSelection() {
      this.commitSelection(this.selectedTaxonomyIds);
      this.$emit('authorized', false);
    },
    async acceptSelection(taxonomyIds) {
      // Guardamos la información de this.items en esta auxiliar porque al reiniciar el componente de autocomplete
      // se lanzará de nuevo una búsqueda y se vaciarán los items, que vamos a necesitar después.
      this.previousSearchItems = this.items;
      const taxonomiesToAdd = difference(taxonomyIds, this.selectedTaxonomyIds);
      const taxonomiesToDelete = difference(this.selectedTaxonomyIds, taxonomyIds);

      // Authorize operation if action is defined
      if (taxonomiesToAdd.length > 0
        && this.auth.actions.ADD) {
        // We are adding taxonomies
        const validate = await this.authorizeOperation(this.auth.actions.ADD, taxonomiesToAdd);
        if (!validate) {
          this.rejectSelection();
          return;
        }
      }

      if (taxonomiesToDelete.length > 0
        && this.auth.actions.DELETE) {
        // We are deleting taxonomies
        const validate = await this.authorizeOperation(this.auth.actions.DELETE, taxonomiesToDelete);
        if (!validate) {
          this.rejectSelection();
          return;
        }
      }
      // Proceed
      this.$emit('authorized', true);
      this.commitSelection(taxonomyIds);
      this.searchText = '';
    },
    commitSelection(selectedTaxonomyIds) {
      // Search in current selector items + previously selected items
      const taxonomies = uniqBy(
        this.previousSearchItems.map(item => Object.assign({}, item.entity))
          .concat(this.selectedItems),
        // UniqBy criteria
        item => item.id,
      );

      if (this.allowRootValue
        && selectedTaxonomyIds.slice(-1)[0] === rootValue) {
        this.selectedItems = [this.parentNullItem];
      } else if (this.limit === 1) {
        const selectedTaxonomyId = selectedTaxonomyIds.slice(-1)[0];
        this.selectedItems = toArray(taxonomies.find(item => item.id === selectedTaxonomyId));
      } else {
        this.selectedItems = selectedTaxonomyIds.map(id => taxonomies.find(taxonomy => taxonomy.id === id));
      }

      if (this.limitReached) {
        this.$refs.selector.blur();
        if (this.limit > 1) this.items = [];
      }
      this.$emit('input', this.normalizedSelectedItems);
    },
    acceptSearcherValue(selectedEntities) {
      this.selectedItems = selectedEntities;
      if (this.limitReached
        && this.limit > 1) {
        this.items = [];
      }
      this.$emit('input', this.normalizedSelectedItems);
    },
    onBlur() {
      this.$emit('blur', this.normalizedSelectedItems);
    },
    getDefaultSiteId() {
      if (this.siteId) {
        return this.siteId;
      }

      // Return default site
      if (!this.mediaId) {
        return this.currentSiteId;
      }

      // Return the media first site
      const sites = this.$store.getters.getMediaIdSites(this.mediaId);
      if (sites.length > 0) {
        return sites[0].id;
      }

      return null;
    },
    async fetchInitialItems(items) {
      // Omit fetch parentNullItem, it isn't a real item
      if (items === rootValue) {
        items = [this.parentNullItem];
      }
      // The selector always work with an array
      const values = toArray(items);
      // If the taxonomy is an id, fetch it
      const taxonomies = await Promise.all(values.map(async (item) => {
        if (isNumber(item) || isString(item)) {
          return this.fetchTaxonomy(item);
        }
        return item;
      }));

      // Filter invalid values
      return taxonomies.filter(item => !!item);
    },

    /**
     * Fetches the information for the given taxonomy identifier.
     * Tries to find the taxonomy in the local items; if it isn't found, fetches it's information
     * from the server.
     */
    async fetchTaxonomy(taxonomyId) {
      const localItem = this.items.find(t => t.entityId === taxonomyId);
      if (localItem && localItem.entity) {
        return localItem.entity;
      }
      return api.fetch(taxonomyId);
    },

    /**
     * @typedef {{}} TaxonomySelectorItem
     * @property {number} id
     * @property {string} name
     * @property {string} icon
     * @property {string} avatar
     */

    /**
     * Maps a taxonomy to a TaxonomySelectorItem.
     * @param {TaxonomyCard} taxonomy
     * @returns {TaxonomySelectorItem}
     */
    formatSelectorItem(taxonomy) {
      const ancestors = taxonomy.ancestors || [];
      const selectorItem = {
        id: taxonomy.id,
        name: ancestors.concat(taxonomy)
          .map(tax => tax.name)
          .join(' / '),
        icon: taxonomy.icon || get(taxonomy, 'list.icon.name') || typeToIcon(taxonomy.type),
        type: taxonomy.type,
        siteAlias: taxonomy.site ? (taxonomy.site.alias || null) : null,
      };
      if (taxonomy.type === 'author') {
        selectorItem.avatar = taxonomy.avatarUrl;
      }
      return selectorItem;
    },
    // eslint-disable-next-line
    searchByText: debounce(function (event) {
      if (this.limitReached && this.limit > 1) return;
      const searchText = isObject(event) ? event.target.value : event;
      if (this.searchText === searchText) return;
      this.searchText = searchText;

      /** @event Searchable#setFilters */
      const newFilters = cloneDeep(this.filters);
      // There isn't and there wasn't search text on filters, abort search
      if (!this.searchText && !this.filters.searchText) {
        return;
      }
      if (this.searchText) {
        newFilters.text = this.searchText;
      } else if (this.filters.text) {
        delete newFilters.text;
      }

      const defaultSort = this.sort || this.defaultSort;
      if (isUndefined(newFilters.sort)
        && !isEmpty(defaultSort)) {
        newFilters.sort = defaultSort;
      }

      this.setFilters(newFilters);
    }, 400),
    // Overwrite mixing setFilters to allow set filters of current component instance
    setFilters(filters) {
      this.filters = filters;
    },
    generateItemRoute(item) {
      const route = { name: 'entity.editor', params: { id: item.id, entity: item.type } };
      if (item.siteAlias) {
        route.params.site = item.siteAlias;
      }
      return route;
    },
  },
};
</script>

<style lang="stylus">
.nx-taxonomy-selector-wrapper {
  position: relative

  .nx-taxonomy-selector-limit {
    position: absolute
    right: 0
    bottom: 0
    font-size: 12px

    &.reached {
      color: red
    }
  }

  .nx-chip--edit {
    pointer-events: all;
    text-decoration: none;
    color: inherit;
    display: contents;
  }
}

.input-group--select.input-group--focused.input-group--open .input-group__append-icon:not(.input-group__icon-clearable)
  transform: none
</style>
